import { useMemo } from 'react';
import { useNavigate } from 'react-router';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
} from '@tanstack/react-table';
import { useTranslation } from '@/lib/i18n';
import {
  labelOf,
  CLAIM_REASON_CODE_LABELS,
} from '@/lib/enumLabels';
import type { ClaimSort, ClaimSummary } from '@/lib/api-admin/types';

type ClaimSummaryExtended = ClaimSummary & {
  buyerName?: string | null;
  // 주문자명 통일(order #145) — 주문서 주문자명. buyerName 은 회원 계정 이름.
  ordererName?: string | null;
  buyerEmail?: string | null;
  // QA-607(order #127) — 주문일시(ISO UTC), 대표 신청 상품의 상품코드·자체상품코드. 없으면 null.
  orderedAt?: string | null;
  productCode?: string | null;
  selfProductCode?: string | null;
};
import {
  CLAIM_SORTABLE_COLUMNS,
  toggleClaimSort,
} from '@/stores/useClaimFiltersStore';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '@/components/ui';
import { ClaimStatusPill } from './ClaimStatusPill';
import { CLAIM_LIST_STATUS_LABELS, CLAIM_LIST_TYPE_LABELS } from '../lib/claimListLabels';
import { memberNameOf, ordererNameOf } from '@/features/orders/lib/ordererName';

export interface ClaimsTableProps {
  items: ReadonlyArray<ClaimSummaryExtended>;
  sort: ClaimSort;
  onSortChange: (sort: ClaimSort) => void;
  /** QA-530 일괄 승인·반려용 선택 — 넘기면 주문번호 왼쪽에 체크박스를 그린다(주문 관리와 같은 모양). */
  selectedIds?: ReadonlyArray<string>;
  onToggleRow?: (claimId: string) => void;
  onToggleAll?: (claimIds: string[]) => void;
}

export const parseClaimSort = (sort: ClaimSort): { id: string; dir: 'asc' | 'desc' } => {
  const [id, dir] = sort.split(',');
  return { id: id ?? 'requestedAt', dir: dir === 'asc' ? 'asc' : 'desc' };
};

const formatMoney = (m: { amount: number; currencyCode: string } | null | undefined): string => {
  if (!m) return '-';
  return `${m.amount.toLocaleString()} ${m.currencyCode}`;
};

const formatDateTime = (iso: string | null | undefined): string => {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const pad = (n: number) => `${n}`.padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export const ClaimsTable = ({ items, sort, onSortChange, selectedIds, onToggleRow, onToggleAll }: ClaimsTableProps) => {
  const { t } = useTranslation('claims');
  const navigate = useNavigate();
  const { id: activeSortId, dir: activeSortDir } = parseClaimSort(sort);
  const selectable = Boolean(selectedIds && onToggleRow && onToggleAll);
  const allIds = items.map((c) => c.claimId);
  const allChecked = allIds.length > 0 && allIds.every((id) => selectedIds?.includes(id));

  const columns = useMemo<ColumnDef<ClaimSummaryExtended>[]>(
    () => [
      {
        id: 'orderNo',
        header: () => t('claims.columns.orderNo'),
        cell: ({ row }) => (
          <span
            className="block max-w-[160px] overflow-hidden text-ellipsis whitespace-nowrap"
            title={row.original.orderNo ?? '-'}
          >
            {row.original.orderNo ?? '-'}
          </span>
        ),
      },
      {
        // QA-607 주문일시.
        id: 'orderedAt',
        header: () => '주문일시',
        cell: ({ row }) => <span className="whitespace-nowrap">{formatDateTime(row.original.orderedAt)}</span>,
      },
      {
        // QA-626 [구매자] → [주문자].
        id: 'buyer',
        header: () => '주문자',
        cell: ({ row }) => {
          const name = ordererNameOf(row.original);
          const member = memberNameOf(row.original);
          const email = row.original.buyerEmail;
          return (
            <div>
              <div
                className="max-w-[140px] overflow-hidden text-ellipsis whitespace-nowrap text-sm font-medium"
                style={{ color: 'var(--content-strong)' }}
                title={name ?? '-'}
              >
                {name ?? '-'}
              </div>
              {member ? (
                <div className="text-xs" style={{ color: 'var(--content-muted)' }} data-testid="claim-member-name">회원명 {member}</div>
              ) : null}
              {email ? (
                <div className="text-xs" style={{ color: 'var(--content-muted)' }}>{email}</div>
              ) : null}
            </div>
          );
        },
      },
      {
        id: 'claimType',
        header: () => t('claims.columns.claimType'),
        accessorFn: (row) => labelOf(CLAIM_LIST_TYPE_LABELS, row.claimType),
      },
      {
        // QA-626 처리 상태(신청·승인·반려·회수중·환불대기·완료·실패).
        id: 'claimStatus',
        header: () => t('claims.columns.claimStatus'),
        accessorFn: (row) => labelOf(CLAIM_LIST_STATUS_LABELS, row.claimStatus),
      },
      {
        id: 'representativeProductName',
        header: () => '상품명',
        cell: ({ row }) => {
          const name = row.original.representativeProductName ?? '-';
          const extra = row.original.additionalItemCount ?? 0;
          return (
            <span className="flex items-center gap-1">
              <span
                className="block max-w-[200px] overflow-hidden text-ellipsis whitespace-nowrap"
                title={name}
              >
                {name}
              </span>
              {extra > 0 ? (
                <span className="shrink-0 text-[color:var(--content-muted)]">
                  {t('claims.list.additionalCount', { count: extra })}
                </span>
              ) : null}
            </span>
          );
        },
      },
      {
        // QA-607 대표 신청 상품의 상품코드(아래 줄 자체상품코드).
        id: 'productCode',
        header: () => '상품코드',
        cell: ({ row }) =>
          row.original.productCode || row.original.selfProductCode ? (
            <div className="whitespace-nowrap leading-tight">
              <div>{row.original.productCode ?? '-'}</div>
              {row.original.selfProductCode ? (
                <div className="text-xs" style={{ color: 'var(--content-muted)' }}>{row.original.selfProductCode}</div>
              ) : null}
            </div>
          ) : (
            <span style={{ color: 'var(--content-muted)' }}>-</span>
          ),
      },
      {
        id: 'reasonCode',
        header: () => t('claims.columns.reasonCode'),
        accessorFn: (row) => labelOf(CLAIM_REASON_CODE_LABELS, row.reasonCode),
      },
      {
        id: 'requestedAt',
        header: () => t('claims.columns.requestedAt'),
        accessorFn: (row) => formatDateTime(row.requestedAt),
      },
      {
        id: 'expectedRefundAmount',
        header: () => t('claims.columns.expectedRefundAmount'),
        accessorFn: (row) => formatMoney(row.expectedRefundAmount),
      },
      {
        id: 'displayStatus',
        header: () => t('claims.columns.status'),
        cell: ({ row }) => (
          <ClaimStatusPill
            displayStatus={row.original.displayStatus}
            displayLabelCode={
              (row.original as { displayLabelCode?: string | null }).displayLabelCode
            }
            claimId={row.original.claimId}
          />
        ),
      },
    ],
    [t],
  );

  const table = useReactTable({
    data: items as ClaimSummaryExtended[],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (items.length === 0) {
    return (
      <p
        className="py-8 text-center text-sm text-[color:var(--content-muted)]"
        data-testid="claims-empty"
      >
        {t('claims.list.empty')}
      </p>
    );
  }

  return (
    <Table data-testid="claims-table">
      <TableHeader style={{ background: 'var(--surface-1)' }}>
        <TableRow>
          {selectable ? (
            <TableHead scope="col" style={{ width: '44px' }}>
              <input
                type="checkbox"
                aria-label={t('claims.list.selectAll', '전체 선택')}
                checked={allChecked}
                onChange={() => onToggleAll?.(allChecked ? [] : allIds)}
                data-testid="claims-select-all"
              />
            </TableHead>
          ) : null}
          {table.getHeaderGroups()[0]?.headers.map((header) => {
            const isSortable = CLAIM_SORTABLE_COLUMNS.has(header.column.id);
            const isActive = activeSortId === header.column.id;
            return (
              <TableHead key={header.id} scope="col" className="whitespace-nowrap">
                {isSortable ? (
                  <button
                    type="button"
                    className="inline-flex items-center gap-1"
                    onClick={() => onSortChange(toggleClaimSort(sort, header.column.id))}
                    aria-label={t('claims.list.sortBy', {
                      column: t(`claims.columns.${header.column.id}`),
                    })}
                  >
                    {flexRender(header.column.columnDef.header, header.getContext())}
                    <span aria-hidden="true">
                      {isActive ? (activeSortDir === 'asc' ? '↑' : '↓') : ''}
                    </span>
                  </button>
                ) : (
                  flexRender(header.column.columnDef.header, header.getContext())
                )}
              </TableHead>
            );
          })}
        </TableRow>
      </TableHeader>
      <TableBody>
        {table.getRowModel().rows.map((row) => {
          const claim = row.original;
          const isSelected = Boolean(selectedIds?.includes(claim.claimId));
          return (
            <TableRow
              key={row.id}
              className="cursor-pointer"
              style={{ height: 'var(--density-compact-row-h)' }}
              onClick={(e) => {
                if ((e.target as HTMLElement).tagName === 'INPUT') return;
                navigate(`/claims/${claim.claimId}`);
              }}
              data-claim-id={claim.claimId}
              data-selected={isSelected ? 'true' : 'false'}
              data-testid="claims-row"
            >
              {selectable ? (
                <TableCell className="px-3">
                  <input
                    type="checkbox"
                    aria-label={t('claims.list.selectRow', { orderNo: claim.orderNo ?? '', defaultValue: '{{orderNo}} 선택' })}
                    checked={isSelected}
                    onChange={() => onToggleRow?.(claim.claimId)}
                    onClick={(e) => e.stopPropagation()}
                    data-testid="claims-select-row"
                  />
                </TableCell>
              ) : null}
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id} className="whitespace-nowrap">
                  {flexRender(
                    cell.column.columnDef.cell ?? ((ctx) => ctx.getValue() as string),
                    cell.getContext(),
                  )}
                </TableCell>
              ))}
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};
